import React, { useState, useEffect } from 'react';
import { ListGroup, Accordion } from 'react-bootstrap';
import { useAccordionToggle } from 'react-bootstrap/AccordionToggle';
import { GetRevenueBoard, GetRevenueBoardCsv, GetTeams } from '../../api/JobsAPI';

function RevenueCustomerToggle({ customer, eventKey, callback }) {
    const decoratedOnClick = useAccordionToggle(
        eventKey,
        () => callback && callback(eventKey)
    );

    return (
        <ListGroup.Item className="click-list" onClick={decoratedOnClick}>
            <div className="row">
                <div className="col-md-3">
                    {customer.firstName} {customer.lastName}
                </div>
                <div className="col-md-2">
                    {customer.customerID}
                </div>
                <div className="col-md-2">
                    {formatDate(customer.firstCompletedJobDate)}
                </div>
                <div className="col-md-1">
                    {customer.jobCountInRange}
                </div>
                <div className="col-md-3">
                    {formatCurrency(customer.revenueInRange)}
                </div>
                <div className="col-md-1">
                    {formatNumber(customer.manHoursInRange)}
                </div>
            </div>
        </ListGroup.Item>
    );
}

const RevenueCustomerAccordion = (props) => {
    return (
        <Accordion className="grouped-accordion">
            <RevenueCustomerToggle
                customer={props.customer}
                eventKey={props.eventKey}
            />

            <Accordion.Collapse eventKey={props.eventKey}>
                <ListGroup.Item className="click-list-subitem">
                    {props.jobs.length === 0 ?
                        <div>No jobs found for this customer.</div>
                        :
                        <>
                            <div className="row font-weight-bold mb-2">
                                <div className="col-md-2">Job ID</div>
                                <div className="col-md-2">Start Date</div>
                                <div className="col-md-2">Revenue</div>
                                <div className="col-md-1">Hours</div>
                                <div className="col-md-1">Team</div>
                                <div className="col-md-1">Sequence</div>
                                <div className="col-md-3">First Completed</div>
                            </div>

                            {props.jobs.map((job) =>
                                <div className="row revenue-job-row" key={`RevenueJob-${job.jobID}`}>
                                    <div className="col-md-2">{job.jobID}</div>
                                    <div className="col-md-2">{formatDate(job.startDate)}</div>
                                    <div className="col-md-2">{formatCurrency(job.jobRevenue)}</div>
                                    <div className="col-md-1">{formatNumber(job.totalManHours)}</div>
                                    <div className="col-md-1">{job.team}</div>
                                    <div className="col-md-1">{job.jobSequence}</div>
                                    <div className="col-md-3">{formatDate(job.firstCompletedJobDate)}</div>
                                </div>
                            )}
                        </>
                    }
                </ListGroup.Item>
            </Accordion.Collapse>
        </Accordion>
    );
};

const RevenueCustomerList = (props) => {
    return (
        <ListGroup className="my-3">
            <ListGroup.Item className="fchp-data-list">
                {props.title}
            </ListGroup.Item>

            <ListGroup.Item className="fchp-data-list">
                <div className="row">
                    <div className="col-md-3">Customer</div>
                    <div className="col-md-2">Customer ID</div>
                    <div className="col-md-2">First Job</div>
                    <div className="col-md-1">Jobs</div>
                    <div className="col-md-3">Revenue</div>
                    <div className="col-md-1">Hours</div>
                </div>
            </ListGroup.Item>

            {props.customers.length === 0 &&
                <ListGroup.Item>No Customers To Display</ListGroup.Item>
            }

            {props.customers.map((customer) => {
                const customerJobs = props.jobs.filter((job) => {
                    return job.customerID === customer.customerID;
                });

                return (
                    <RevenueCustomerAccordion
                        key={`${props.customerType}-${customer.customerID}`}
                        eventKey={`${props.customerType}-${customer.customerID}`}
                        customer={customer}
                        jobs={customerJobs}
                    />
                );
            })}
        </ListGroup>
    );
};

const RevenueSummary = (props) => {
    const formatPercent = (value) => {
        const number = Number(value || 0);

        return `${number.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}%`;
    };

    return (
        <>
            <div className="row my-3">
                <div className="col-md-12">
                    <ListGroup>
                        <ListGroup.Item className="fchp-data-list">
                            Totals
                        </ListGroup.Item>

                        <ListGroup.Item className="revenue-summary-row">
                            <div className="row">
                                <div className="col-md-6">

                                    <div className="row revenue-summary-totals-space-l">
                                        <div className="col-md-3">
                                            <div className="revenue-summary-label">Customers</div>
                                            <div className="revenue-summary-value">
                                                {formatNumber(props.summary.newCustomerCount + props.summary.repeatCustomerCount)}
                                            </div>
                                        </div>

                                        <div className="col-md-3">
                                            <div className="revenue-summary-label">Jobs</div>
                                            <div className="revenue-summary-value">
                                                {formatNumber(props.summary.newCustomerJobCount + props.summary.repeatCustomerJobCount)}
                                            </div>
                                        </div>

                                        <div className="col-md-3">
                                            <div className="revenue-summary-label">Revenue</div>
                                            <div className="revenue-summary-value">
                                                {formatCurrency(props.summary.newCustomerRevenue + props.summary.repeatCustomerRevenue)}
                                            </div>
                                        </div>

                                        <div className="col-md-3">
                                            <div className="revenue-summary-label">Avg / Customer</div>
                                            <div className="revenue-summary-value">
                                                {formatCurrency(props.summary.newAvgRevenuePerCustomer + props.summary.repeatAvgRevenuePerCustomer)}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6">

                                    <div className="row revenue-summary-totals-space-r">

                                        <div className="col-md-3">
                                            <div className="revenue-summary-label">Avg / Job</div>
                                            <div className="revenue-summary-value">
                                                {formatCurrency(props.summary.newAvgRevenuePerJob + props.summary.repeatAvgRevenuePerJob)}
                                            </div>
                                        </div>

                                        <div className="col-md-3">
                                            <div className="revenue-summary-label">% of Total Revenue</div>
                                            <div className="revenue-summary-value">
                                                {formatPercent(props.summary.newRevenuePctOfTotal + props.summary.repeatRevenuePctOfTotal)}
                                            </div>
                                        </div>

                                        <div className="col-md-3">
                                            <div className="revenue-summary-label">Man Hours</div>
                                            <div className="revenue-summary-value">
                                                {formatNumber(props.summary.newCustomerManHours + props.summary.repeatCustomerManHours)}
                                            </div>
                                        </div>

                                        <div className="col-md-3">
                                            <div className="revenue-summary-label">Rev / Man Hour</div>
                                            <div className="revenue-summary-value">
                                                {formatCurrency((props.summary.newCustomerRevenue + props.summary.repeatCustomerRevenue) /
                                                    (props.summary.newCustomerManHours + props.summary.repeatCustomerManHours))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </ListGroup.Item>
                    </ListGroup>
                </div>
            </div>
            <div className="row my-3">
                <div className="col-md-6">
                    <ListGroup>
                        <ListGroup.Item className="fchp-data-list">
                            New Customers
                        </ListGroup.Item>

                        <ListGroup.Item className="revenue-summary-row">
                            <div className="row">
                                <div className="col-md-3">
                                    <div className="revenue-summary-label">Customers</div>
                                    <div className="revenue-summary-value">
                                        {formatNumber(props.summary.newCustomerCount)}
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <div className="revenue-summary-label">Jobs</div>
                                    <div className="revenue-summary-value">
                                        {formatNumber(props.summary.newCustomerJobCount)}
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <div className="revenue-summary-label">Revenue</div>
                                    <div className="revenue-summary-value">
                                        {formatCurrency(props.summary.newCustomerRevenue)}
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <div className="revenue-summary-label">Man Hours</div>
                                    <div className="revenue-summary-value">
                                        {formatNumber(props.summary.newCustomerManHours)}
                                    </div>
                                </div>
                            </div>
                        </ListGroup.Item>

                        <ListGroup.Item className="revenue-summary-row">
                            <div className="row">
                                <div className="col-md-3">
                                    <div className="revenue-summary-label">Avg / Customer</div>
                                    <div className="revenue-summary-value">
                                        {formatCurrency(props.summary.newAvgRevenuePerCustomer)}
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <div className="revenue-summary-label">Avg / Job</div>
                                    <div className="revenue-summary-value">
                                        {formatCurrency(props.summary.newAvgRevenuePerJob)}
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <div className="revenue-summary-label">% of Total Revenue</div>
                                    <div className="revenue-summary-value">
                                        {formatPercent(props.summary.newRevenuePctOfTotal)}
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <div className="revenue-summary-label">Rev / Man Hour</div>
                                    <div className="revenue-summary-value">
                                        {formatCurrency(props.summary.newCustomerRevenue / props.summary.newCustomerManHours)}
                                    </div>
                                </div>
                            </div>
                        </ListGroup.Item>
                    </ListGroup>
                </div>

                <div className="col-md-6">
                    <ListGroup>
                        <ListGroup.Item className="fchp-data-list">
                            Repeat Customers
                        </ListGroup.Item>

                        <ListGroup.Item className="revenue-summary-row">
                            <div className="row">
                                <div className="col-md-3">
                                    <div className="revenue-summary-label">Customers</div>
                                    <div className="revenue-summary-value">
                                        {formatNumber(props.summary.repeatCustomerCount)}
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <div className="revenue-summary-label">Jobs</div>
                                    <div className="revenue-summary-value">
                                        {formatNumber(props.summary.repeatCustomerJobCount)}
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <div className="revenue-summary-label">Revenue</div>
                                    <div className="revenue-summary-value">
                                        {formatCurrency(props.summary.repeatCustomerRevenue)}
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <div className="revenue-summary-label">ManHours</div>
                                    <div className="revenue-summary-value">
                                        {formatNumber(props.summary.repeatCustomerManHours)}
                                    </div>
                                </div>
                            </div>
                        </ListGroup.Item>

                        <ListGroup.Item className="revenue-summary-row">
                            <div className="row">
                                <div className="col-md-3">
                                    <div className="revenue-summary-label">Avg / Customer</div>
                                    <div className="revenue-summary-value">
                                        {formatCurrency(props.summary.repeatAvgRevenuePerCustomer)}
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <div className="revenue-summary-label">Avg / Job</div>
                                    <div className="revenue-summary-value">
                                        {formatCurrency(props.summary.repeatAvgRevenuePerJob)}
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <div className="revenue-summary-label">% of Total Revenue</div>
                                    <div className="revenue-summary-value">
                                        {formatPercent(props.summary.repeatRevenuePctOfTotal)}
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <div className="revenue-summary-label">Rev / Man Hour</div>
                                    <div className="revenue-summary-value">
                                        {formatCurrency(props.summary.repeatCustomerRevenue / props.summary.repeatCustomerManHours)}
                                    </div>
                                </div>
                            </div>
                        </ListGroup.Item>
                    </ListGroup>
                </div>
            </div>
        </>
    );
};

const RevenueBoard = (props) => {
    const [startDate, setStartDate] = useState(getDefaultStartDate());
    const [endDate, setEndDate] = useState(getDefaultEndDate());
    const [filterTeams, setFilterTeams] = useState(false);
    const [teams, setTeams] = useState([]);
    const [selectedTeamIDs, setSelectedTeamIDs] = useState([]);

    const [summary, setSummary] = useState({
        newCustomerCount: 0,
        newCustomerJobCount: 0,
        newCustomerRevenue: 0,
        newAvgRevenuePerCustomer: 0,
        newAvgRevenuePerJob: 0,
        newRevenuePctOfTotal: 0,

        repeatCustomerCount: 0,
        repeatCustomerJobCount: 0,
        repeatCustomerRevenue: 0,
        repeatAvgRevenuePerCustomer: 0,
        repeatAvgRevenuePerJob: 0,
        repeatRevenuePctOfTotal: 0
    });

    const [customers, setCustomers] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        document.title = "Revenue Board";
        loadTeams();
        loadData();
    }, []);

    const loadTeams = () => {
        GetTeams()
            .then((res) => {
                setTeams(res.teams || []);
            })
            .catch((err) => {
                console.error(err);
                setErrorMessage('Unable to load teams.');
            });
    };

    const toggleTeamSelection = (teamID) => {
        setSelectedTeamIDs((prev) => {
            if (prev.includes(teamID)) {
                return prev.filter((id) => id !== teamID);
            }

            return [...prev, teamID];
        });
    };

    const selectAllTeams = () => {
        setSelectedTeamIDs((teams || []).map((team) => team.teamID));
    };

    const clearAllTeams = () => {
        setSelectedTeamIDs([]);
    };

    const buildFilters = () => {
        return {
            startDate: startDate,
            endDate: endDate,
            teamIDs: filterTeams ? selectedTeamIDs : []
        };
    };

    const loadData = () => {
        setLoading(true);
        setErrorMessage('');

        GetRevenueBoard(buildFilters())
            .then((res) => {
                setSummary(res.summary || {});
                setCustomers(res.customers || []);
                setJobs(res.jobs || []);
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setErrorMessage('Unable to load revenue board.');
                setLoading(false);
            });
    };

    const exportExcel = () => {
        setErrorMessage('');

        GetRevenueBoardCsv(buildFilters())
            .then((blob) => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');

                link.href = url;
                link.download = `RevenueBoard_${startDate}_${endDate}.csv`;

                document.body.appendChild(link);
                link.click();

                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch((err) => {
                console.error(err);
                setErrorMessage('Unable to export revenue board CSV.');
            });
    };

    const newCustomers = customers.filter((customer) => {
        return customer.customerType === 'New';
    });

    const repeatCustomers = customers.filter((customer) => {
        return customer.customerType === 'Repeat';
    });

    return (
        <div className='app-main container-fluid'>
            <ListGroup className="my-3">
                <ListGroup.Item className="fchp-data-list">
                    Revenue Board
                </ListGroup.Item>

                <ListGroup.Item className="fchp-data-list">
                    <div className="row">
                        <div className="col-md-3">
                            Start Date
                        </div>
                        <div className="col-md-3">
                            End Date
                        </div>
                        <div className="col-md-2">
                            Team Filter
                        </div>
                        <div className="col-md-4">
                            Actions
                        </div>
                    </div>
                </ListGroup.Item>

                <ListGroup.Item>
                    <div className="row">
                        <div className="col-md-3">
                            <input
                                type="date"
                                className="fchp-input-100"
                                value={startDate}
                                onChange={(event) => setStartDate(event.target.value)}
                            />
                        </div>

                        <div className="col-md-3">
                            <input
                                type="date"
                                className="fchp-input-100"
                                value={endDate}
                                onChange={(event) => setEndDate(event.target.value)}
                            />
                        </div>

                        <div className="col-md-2 d-flex align-items-center">
                            <div className="form-check">
                                <input
                                    id="RevenueBoardFilterTeams"
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={filterTeams}
                                    onChange={(event) => {
                                        const checked = event.target.checked;
                                        setFilterTeams(checked);

                                        if (!checked) {
                                            setSelectedTeamIDs([]);
                                        }
                                    }}
                                />
                                <label className="form-check-label" htmlFor="RevenueBoardFilterTeams">
                                    Filter By Team
                                </label>
                            </div>
                        </div>

                        <div className="col-md-4">
                            <button
                                type="button"
                                className="fchp-button mr-2"
                                onClick={loadData}
                                disabled={loading}
                            >
                                <i className="fas fa-search mr-1"></i>
                                Load Report
                            </button>

                            <button
                                type="button"
                                className="fchp-button"
                                onClick={exportExcel}
                                disabled={loading}
                            >
                                <i className="fas fa-file-excel mr-1"></i>
                                Export Excel
                            </button>
                        </div>
                    </div>

                    {filterTeams &&
                        <div className="mt-3">
                            <div className="mb-2">
                                <button
                                    type="button"
                                    className="fchp-button mr-2"
                                    onClick={selectAllTeams}
                                    disabled={teams.length === 0}
                                >
                                    Select All Teams
                                </button>

                                <button
                                    type="button"
                                    className="fchp-button"
                                    onClick={clearAllTeams}
                                    disabled={selectedTeamIDs.length === 0}
                                >
                                    Clear Teams
                                </button>
                            </div>

                            <div className="row">
                                {teams.length === 0 &&
                                    <div className="col-md-12">
                                        No teams available.
                                    </div>
                                }
                                {teams.map((team) => (
                                    <div className="col-md-3 mb-2" key={team.teamID}>
                                        <div className="form-check">
                                            <input
                                                id={`RevenueBoardTeam-${team.teamID}`}
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={selectedTeamIDs.includes(team.teamID)}
                                                onChange={() => toggleTeamSelection(team.teamID)}
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor={`RevenueBoardTeam-${team.teamID}`}
                                            >
                                                {team.name}
                                            </label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    }
                </ListGroup.Item>
            </ListGroup>

            {errorMessage !== '' &&
                <div className="alert alert-danger">
                    {errorMessage}
                </div>
            }

            {loading &&
                <h3 className='text-center m-5'>Loading revenue board...</h3>
            }

            {!loading &&
                <>
                    <RevenueSummary summary={summary} />

                    <div className="row">
                        <div className="col-md-6">
                            <RevenueCustomerList
                                title="New Customers"
                                customerType="New"
                                customers={newCustomers}
                                jobs={jobs}
                            />
                        </div>

                        <div className="col-md-6">
                            <RevenueCustomerList
                                title="Repeat Customers"
                                customerType="Repeat"
                                customers={repeatCustomers}
                                jobs={jobs}
                            />
                        </div>
                    </div>
                </>
            }
        </div>
    );
};

const getDefaultStartDate = () => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);

    return formatDateInput(start);
};

const getDefaultEndDate = () => {
    const today = new Date();
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);

    return formatDateInput(end);
};

const formatDateInput = (date) => {
    const year = date.getFullYear();
    const month = (`0${date.getMonth() + 1}`).slice(-2);
    const day = (`0${date.getDate()}`).slice(-2);

    return `${year}-${month}-${day}`;
};

const formatDate = (value) => {
    if (!value) {
        return '';
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString();
};

const formatCurrency = (value) => {
    const amount = Number(value || 0);

    return amount.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
    });
};

const formatNumber = (value) => {
    const number = Number(value || 0);

    return number.toLocaleString('en-US');
};

export default RevenueBoard;